import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft, ArrowRight, MessageSquareQuote } from "lucide-react";

interface PlanOption {
  name: string;
  price: string;
}

interface StepPricingPlanProps {
  plans: PlanOption[];
  selectedPlan: string;
  onSelectPlan: (plan: string) => void;
  onBack: () => void;
  onNext: () => void;
  isSubmitting?: boolean;
}

const StepPricingPlan = ({ plans, selectedPlan, onSelectPlan, onBack, onNext, isSubmitting }: StepPricingPlanProps) => {
  const isCustomQuote = selectedPlan === "Custom Quote";

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      transition={{ duration: 0.3 }}
      className="space-y-6 pt-6"
    >
      <div className="text-center">
        <h3 className="text-lg font-bold text-slate-800">Select Your Plan</h3>
        <p className="text-sm text-slate-500 mt-1">Choose a plan or request a custom quote.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {plans.map((plan) => {
          const isSelected = selectedPlan === plan.name;
          return (
            <button
              key={plan.name}
              type="button"
              onClick={() => onSelectPlan(plan.name)}
              className={`relative rounded-xl border-2 p-4 text-left transition-all duration-200 ${
                isSelected
                  ? "border-red-500 bg-red-50 shadow-md ring-2 ring-red-200"
                  : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
              }`}
            >
              {isSelected && (
                <div className="absolute -top-2 -right-2 bg-red-500 rounded-full p-0.5">
                  <Check className="h-3.5 w-3.5 text-white" />
                </div>
              )}
              <p className="font-semibold text-slate-800">{plan.name}</p>
              <p className="text-sm font-bold text-red-600 mt-1">{plan.price}</p>
            </button>
          );
        })}
      </div>

      {/* Custom quote option */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => onSelectPlan("Custom Quote")}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200 ${
            isCustomQuote
              ? "bg-emerald-50 text-emerald-700 border-2 border-emerald-400 shadow-sm"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
          }`}
        >
          <MessageSquareQuote className="h-4 w-4" />
          Get a Custom Quote
        </button>
      </div>

      {/* Custom quote confirmation */}
      {isCustomQuote && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3"
        >
          <div className="bg-emerald-500 rounded-full p-1 shrink-0">
            <Check className="h-4 w-4 text-white" />
          </div>
          <p className="text-sm text-emerald-800 font-medium">
            Custom quote requested — we'll prepare a tailored proposal for you.
          </p>
        </motion.div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="gap-2 bg-gradient-to-r from-lime-400 to-green-500 text-white border-0 hover:from-lime-500 hover:to-green-600 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button
          type="button"
          onClick={onNext}
          disabled={!selectedPlan || isSubmitting}
          className="gap-2"
        >
          {isSubmitting ? "Preparing…" : "Next"} <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  );
};

export default StepPricingPlan;
